import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Stack, MenuItem, TextField, Alert } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import AttendanceRecordModal from "../components/attendanceRecords/AttendanceRecordModal";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { usePermissions } from "../hooks/usePermissions";
import {
  getAttendanceRecordsByClassroom,
  resetAttendanceServerStatus,
} from "../redux/actions/attendanceRecordActions";
import { getClassrooms } from "../redux/actions/classroomActions";
import { AttendanceResponseDto } from "../shared/dtos/attendanceRecord/AttendanceResponseDto";

const AttendanceRecordsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    attendanceRecords,
    loadingAttendanceRecords,
    errorLoadingAttendanceRecords,
    serverStatus,
  } = useAppSelector((state) => state.attendanceRecord);

  const { items: classrooms } = useAppSelector((state) => state.classroom);

  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [isRecordModalOpen, setIsRecordModalOpen] = useState<boolean>(false);

  const canRecord = hasPermission("attendance:create");

  useEffect(() => {
    dispatch(getClassrooms());
  }, [dispatch]);

  const fetchRecords = useCallback((): void => {
    if (selectedClass) {
      dispatch(
        getAttendanceRecordsByClassroom({
          classroomId: selectedClass,
          date: selectedDate,
        }),
      );
    }
  }, [dispatch, selectedClass, selectedDate]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;

    if (operation === "bulk-save" && status === 201) {
      setIsRecordModalOpen(false);
      fetchRecords();
    }
    dispatch(resetAttendanceServerStatus());
  }, [serverStatus, dispatch, fetchRecords]);

  const columns = useMemo<TableColumn<AttendanceResponseDto>[]>(
    () => [
      { id: "admissionNumber", label: "Adm No.", minWidth: 100 },
      { id: "studentName", label: "Student Name", minWidth: 200 },
      {
        id: "status",
        label: "Status",
        minWidth: 120,
        format: (v) => <StatusChip status={v as any} />,
      },
      { id: "remarks", label: "Remarks", minWidth: 200 },
      { id: "recordedByTeacherName", label: "Marked By", minWidth: 150 },
    ],
    [],
  );

  return (
    <PageContainer
      title="Attendance Management"
      subtitle="View and record daily or lesson-based attendance."
      actions={
        canRecord && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsRecordModalOpen(true)}
            disabled={!selectedClass}
          >
            Record Attendance
          </AppButton>
        )
      }
    >
      <PageToolbar searchPlaceholder="Search students...">
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <TextField
            select
            label="Classroom"
            size="small"
            sx={{ minWidth: 200 }}
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <MenuItem value="">-- Select Class --</MenuItem>
            {classrooms?.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Date"
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </Stack>
      </PageToolbar>

      {selectedClass ? (
        <StandardTable<AttendanceResponseDto>
          columns={columns}
          data={attendanceRecords}
          loading={loadingAttendanceRecords}
          error={errorLoadingAttendanceRecords}
          onRetry={fetchRecords}
          page={1}
          count={attendanceRecords?.length || 0}
          rowsPerPage={attendanceRecords?.length || 10}
          onPageChange={() => {}}
          onRowsPerPageChange={() => {}}
        />
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please select a classroom and date to view records.
        </Alert>
      )}

      {isRecordModalOpen && (
        <AttendanceRecordModal
          open={isRecordModalOpen}
          onClose={() => setIsRecordModalOpen(false)}
          classroomId={selectedClass}
          date={selectedDate}
        />
      )}
    </PageContainer>
  );
};

export default AttendanceRecordsPage;
